import { lazy } from "react"
import type { FC, LazyExoticComponent, ReactElement } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { ErrorBoundary } from "react-error-boundary"
import { useMount } from "react-use"

import { ErrorHint, Loadable, Modal as ModalComponent } from "@/3514/components"
import { EModalId, type IModal, type TModalDispatcher, useModalDispatcher, useModalSelector } from "@/3514/store/modal"

import elements from "./attendance.modal.elements"
import classes from "./attendance.modal.styles"
import type {
    TAttendanceEarlyModalProps as TEarlyModalProps,
    TAttendanceModalProps as TModalProps
} from "./attendance.modal.ui.types"
import uiUtils, { isEnoughTimeToMarkAttendance } from "./attendance.modal.ui.utils"

const LazyBody: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> => import("./attendance.modal.body.component")
)

const AttendanceModalContainer: FC = (): ReactElement => {
    const { props: modalProps }: IModal<TModalProps> = useModalSelector<TModalProps>(EModalId.Attendance)

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const qc: QueryClient = useQueryClient()

    const isPossibleToMark: boolean = isEnoughTimeToMarkAttendance(
        modalProps.sessionStartTime,
        modalProps.minHoursToMark
    )

    useMount((): void => {
        if (isPossibleToMark) return

        modalDispatcher.hideModal(EModalId.Attendance)

        modalDispatcher.showModal<TEarlyModalProps>(EModalId.AttendanceEarly, {
            advanceTimeInHours: modalProps.minHoursToMark
        })
    })

    return (
        <ModalComponent
            modalId={EModalId.Attendance}
            title={<elements.SessionInfo {...uiUtils.getHeaderInfoProps({ modalProps, qc })} />}
            modalBodyClassName={classes.attendance.body()}
            modalHeaderClassName={classes.attendance.header()}
            withDisabledBodyScroll
            withStickyHeading
            withCloseIcon
            withHideOnEscapeClick
        >
            <ErrorBoundary fallback={<ErrorHint isVisible />}>
                <Loadable isReady={isPossibleToMark} component={LazyBody} loadingFallback={<elements.Loader />} />
            </ErrorBoundary>
        </ModalComponent>
    )
}

AttendanceModalContainer.displayName = "AttendanceModalContainer"

export default AttendanceModalContainer
